import type { DeclinationReceived, Exercise, LayoutItem, Order, OrderChange, OrderFieldSpecs } from "../../api/types";
import { t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { plural } from "../../plural";
import { fieldText } from "./outdated";

/** Paragraphes mis à jour d'après le dossier (cohérence carte ↔ OPO, demande d'Alexis du 13/10/2026) : après une
 *  écriture de la carte, de la composition ou des Données initiales, le serveur recompose l'OPO et pose un repère
 *  sur chaque paragraphe imprimé qui a changé (`Order.changes`). L'aperçu les surligne (fond jaune pâle et trait de
 *  révision dans la marge), l'éditeur les teinte et montre « Avant / après » ; rien de tout cela n'est imprimé.
 *  Les repères sont communs au dossier ; seule la pulsation dépend du poste : un repère plus récent que le dernier
 *  « vu » de ce poste (rangé sous la licence) pulse et ouvre le bandeau de relecture. Fonctions pures (hors
 *  lecture et écriture du « vu »), testées par changes.test.ts. */

/** Métriques verticales de la police de l'iPad (SF Pro), reprises par le moteur de mise en page. */
const ASCENDER = 1950 / 2048;
const DESCENDER = 494 / 2048;
/** Au-delà de cet écart entre deux lignes d'un même champ (en tailles de police), un nouveau bloc commence. */
const RUN_GAP = 1.6;

type TextItem = Extract<LayoutItem, { t: "text" }>;

/** Bloc de lignes contiguës d'un champ sur une page, en coordonnées de la page à l'écran (haut à gauche). */
export interface Zone {
  field: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Zones des paragraphes d'une page : lignes d'un même champ découpées en blocs contigus (écart vertical de moins de
 *  1,6 fois la taille de police), plusieurs rectangles par champ au besoin. Un champ imprimé à deux endroits (nom de
 *  la formation dans l'en-tête et dans l'articulation) ou en plusieurs paragraphes (ennemi initial) ne couvre ainsi
 *  jamais ce qui les sépare. Rangées de haut en bas. */
export function fieldRuns(items: readonly LayoutItem[], height: number): Zone[] {
  const byField = new Map<string, TextItem[]>();
  for (const it of items) {
    if (it.t !== "text" || !it.field) continue;
    const list = byField.get(it.field) ?? [];
    list.push(it);
    byField.set(it.field, list);
  }
  const out: Zone[] = [];
  for (const [field, lines] of byField) {
    // Du haut vers le bas (ordonnée du PDF décroissante), puis de gauche à droite.
    const sorted = [...lines].sort((a, b) => b.y - a.y || a.x - b.x);
    let run: TextItem[] = [];
    const close = () => {
      if (!run.length) return;
      const x1 = Math.min(...run.map((l) => l.x));
      const x2 = Math.max(...run.map((l) => l.x + l.w));
      const top = Math.max(...run.map((l) => l.y + ASCENDER * l.size));
      const bottom = Math.min(...run.map((l) => l.y - DESCENDER * l.size));
      out.push({ field, x: x1 - 3, y: height - top - 2, w: x2 - x1 + 6, h: top - bottom + 4 });
      run = [];
    };
    for (const line of sorted) {
      const last = run[run.length - 1];
      if (last && last.y - line.y > RUN_GAP * Math.max(last.size, line.size)) close();
      run.push(line);
    }
    close();
  }
  return out.sort((a, b) => a.y - b.y || a.x - b.x);
}

/** Repères d'un OPO : aucun pour un OPO validé. */
export function orderChanges(order: Pick<Order, "changes" | "status"> | null | undefined): Record<string, OrderChange> {
  if (!order || order.status === "finalized") return {};
  return order.changes ?? {};
}

/** Étiquettes de la mise en page à surligner → clés des repères qui les portent. */
export function changedZones(changes: Readonly<Record<string, OrderChange>>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const [key, change] of Object.entries(changes)) {
    for (const zone of change.zones?.length ? change.zones : [key]) {
      const list = out.get(zone) ?? [];
      list.push(key);
      out.set(zone, list);
    }
  }
  return out;
}

/** Champ de l'éditeur d'une clé de repère : « sgtiaUnitMissions:<unité> » → « sgtiaUnitMissions » ; texte d'attaque
 *  d'un ME → texte de défense (l'éditeur montre le texte imprimé sous ce champ). */
export function changeField(key: string): string {
  const field = key.split(":")[0] ?? key;
  const me = /^enemyME([12])AttackText$/.exec(field);
  return me ? `enemyME${me[1]}DefenseText` : field;
}

/** Champs de l'éditeur touchés par des repères → clés des repères, dans l'ordre de l'éditeur (champs inconnus des
 *  specs à la fin, dans l'ordre des clés). */
export function changedFields(
  changes: Readonly<Record<string, OrderChange>>,
  specs?: OrderFieldSpecs | null,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const key of Object.keys(changes)) {
    const field = changeField(key);
    const list = out.get(field) ?? [];
    list.push(key);
    out.set(field, list);
  }
  if (!specs) return out;
  const position = new Map(specs.fields.map((f, i) => [f.key, i]));
  const rank = (field: string) => position.get(field) ?? Number.MAX_SAFE_INTEGER;
  return new Map([...out].sort(([a], [b]) => rank(a) - rank(b)));
}

/** Nombre de champs mis à jour par onglet de l'éditeur. */
export function changesByTab(changes: Readonly<Record<string, OrderChange>>, specs: OrderFieldSpecs): Map<number, number> {
  const tabOf = new Map(specs.fields.map((f) => [f.key, f.tab]));
  const out = new Map<number, number>();
  for (const field of changedFields(changes).keys()) {
    const tab = tabOf.get(field);
    if (tab !== undefined) out.set(tab, (out.get(tab) ?? 0) + 1);
  }
  return out;
}

/** Sections de l'ordre (titres des onglets de l'éditeur, traduits) où tombent ces clés, dans l'ordre de l'ordre ;
 *  `unknown` : clés qu'aucun onglet ne range. */
export function changedSections(keys: readonly string[], specs: OrderFieldSpecs): { sections: string[]; unknown: number } {
  const tabOf = new Map(specs.fields.map((f) => [f.key, f.tab]));
  const tabs = new Set<number>();
  let unknown = 0;
  for (const key of keys) {
    const tab = tabOf.get(changeField(key));
    if (tab === undefined) unknown++;
    else tabs.add(tab);
  }
  const sections = specs.tabs.filter((tab) => tabs.has(tab.id)).map((tab) => term(tab.title));
  return { sections, unknown };
}

/** Dernier changement des repères (ISO, comparable comme une chaîne) ; chaîne vide s'il n'y en a pas. */
export function latestChange(changes: Readonly<Record<string, OrderChange>>): string {
  let latest = "";
  for (const change of Object.values(changes)) if (change.at > latest) latest = change.at;
  return latest;
}

/** Clés des repères nouveaux pour ce poste : changés après le dernier « vu » (tous s'il n'y en a pas). */
export function freshKeys(changes: Readonly<Record<string, OrderChange>>, seen: string | null): string[] {
  return Object.entries(changes)
    .filter(([, change]) => !seen || change.at > seen)
    .map(([key]) => key);
}

/** Texte comparable d'une valeur de repère : mission d'une unité (« - nom : mission »), sinon `fieldText`. */
function changeText(value: unknown): string {
  if (value && typeof value === "object" && !Array.isArray(value) && "displayName" in value) return fieldText([value]);
  return fieldText(value);
}

/** « Avant / après » d'un champ de l'éditeur : texte imprimé d'avant le premier changement et texte à jour (missions
 *  du §3.b : les seules unités touchées, une par ligne). Null quand le texte d'avant n'a pas été gardé (trop long). */
export function beforeAfter(
  order: Pick<Order, "fields">,
  changes: Readonly<Record<string, OrderChange>>,
  keys: readonly string[],
): { before: string; after: string } | null {
  const marks = keys.map((key) => [key, changes[key]] as const).filter(([, change]) => change !== undefined);
  if (!marks.length) return null;
  // Texte d'avant perdu (plus de 20 000 caractères) : pas de comparaison. Une unité ajoutée n'en a pas non plus,
  // mais sa ligne « avant » est simplement vide.
  if (marks.some(([key, change]) => !key.includes(":") && (change!.previous === null || change!.previous === undefined))) {
    return null;
  }
  const join = (side: "previous" | "current") =>
    marks
      .map(([key, change]) => {
        const value = side === "previous" ? change!.previous : (change!.current ?? (key.includes(":") ? null : order.fields[key]));
        return changeText(value);
      })
      .filter(Boolean)
      .join("\n");
  return { before: join("previous"), after: join("current") };
}

// --- « Vu » par poste -----------------------------------------------------------------------------------------

/** Préfixe des clés du dernier « vu » (rangées sous la licence : `LICENCE_KEYS`). */
export const SEEN_PREFIX = "generex-order-seen:";

/** Clé du dernier repère vu sur ce poste pour un OPO d'un dossier. */
export function seenKey(exerciseId: string, number: number): string {
  return `${SEEN_PREFIX}${exerciseId}:${number}`;
}

/** Stockage du « vu » (licenceStorage dans l'application, simulé dans les tests). */
export interface SeenStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Dernier « vu » de la session, gardé aussi en mémoire : sans stockage (navigation privée, données du site
 *  bloquées), la relecture aboutit tout de même tant que la page reste ouverte. */
const seenInMemory = new Map<string, string>();

/** Dernier repère vu sur ce poste : le plus récent du stockage et de la session ; null si rien n'est rangé. Stockage
 *  indisponible (navigation privée, données du site effacées) : celui de la session seul, les repères pulsent de
 *  nouveau à la prochaine ouverture, sans autre effet. */
export function readSeen(store: SeenStore | null, exerciseId: string, number: number): string | null {
  const key = seenKey(exerciseId, number);
  const remembered = seenInMemory.get(key) ?? null;
  let stored: string | null = null;
  try {
    stored = store?.getItem(key) ?? null;
  } catch {
    stored = null;
  }
  if (stored && remembered) return stored >= remembered ? stored : remembered;
  return stored ?? remembered;
}

/** Range le dernier repère vu (jamais en arrière), dans la session et, s'il est disponible, dans le stockage. */
export function writeSeen(store: SeenStore | null, exerciseId: string, number: number, at: string): void {
  if (!at) return;
  const key = seenKey(exerciseId, number);
  const remembered = seenInMemory.get(key);
  if (!remembered || remembered < at) seenInMemory.set(key, at);
  try {
    const previous = store?.getItem(key);
    if (previous && previous >= at) return;
    store?.setItem(key, at);
  } catch {
    /* stockage indisponible : la session s'en souvient, la pulsation reviendra à la prochaine ouverture */
  }
}

/** Nom du dossier du chef, s'il est lisible : celui de la déclinaison (`received.issuer`), sinon le dossier émetteur
 *  d'un dossier transmis dans la licence (`extra.chain.from_name`) ; null sinon (« l'échelon supérieur »). */
export function chiefOf(exercise: Exercise | undefined, received: DeclinationReceived | null): string | null {
  if (received?.issuer) return received.issuer;
  const chain = exercise?.parameters.extra?.chain as { from?: unknown; from_name?: unknown } | undefined;
  return chain?.from && typeof chain.from_name === "string" && chain.from_name ? chain.from_name : null;
}

/** Texte du bandeau des paragraphes mis à jour : OPO remis en rédaction (par « Rouvrir », par le dossier ou par
 *  l'ordre du chef), ordre qui a suivi l'ordre du chef (cascade des ordres) ou le dossier. */
export function reviewBannerText(o: {
  count: number;
  sections: string;
  reopened: boolean;
  manualReopen: boolean;
  fromChief: boolean;
  chief: string | null;
  /** Ordre de corps qui a suivi une retouche de son OPLAN (repères de cause « oplan », docs/08 §25). */
  fromOplan?: boolean;
  /** Document affiché : l'OPLAN du corps (docs/08 §25). */
  oplan?: boolean;
}): string {
  const highlighted = plural(o.count, t("paragraphe surligné"), t("paragraphes surlignés"));
  const updated = plural(o.count, t("paragraphe mis à jour"), t("paragraphes mis à jour"));
  if (o.oplan && o.reopened)
    return o.manualReopen
      ? t("OPLAN rouvert et mis à jour d'après le dossier : {paragraphs}. Relisez-les, puis validez-le de nouveau.", { paragraphs: highlighted })
      : t("OPLAN remis en rédaction : le dossier a changé son texte validé ({paragraphs}). Relisez-les, puis validez-le de nouveau.", { paragraphs: highlighted });
  if (o.fromOplan)
    return o.reopened
      ? t("OPO remis en rédaction : l'OPLAN du corps a changé son texte validé ({paragraphs}). Relisez-les, puis validez-le de nouveau.", { paragraphs: highlighted })
      : o.sections
        ? t("L'OPLAN du corps a changé : cet ordre l'a suivi, {paragraphs} ({sections}).", { paragraphs: updated, sections: o.sections })
        : t("L'OPLAN du corps a changé : cet ordre l'a suivi, {paragraphs}.", { paragraphs: updated });
  if (o.reopened) {
    if (o.manualReopen)
      return t("OPO rouvert et mis à jour d'après le dossier : {paragraphs}. Relisez-les, puis validez-le de nouveau.", { paragraphs: highlighted });
    if (o.fromChief)
      return o.chief
        ? t("OPO remis en rédaction : l'ordre de « {name} » a changé son texte validé ({paragraphs}). Relisez-les, puis validez-le de nouveau.", { name: o.chief, paragraphs: highlighted })
        : t("OPO remis en rédaction : l'ordre de l'échelon supérieur a changé son texte validé ({paragraphs}). Relisez-les, puis validez-le de nouveau.", { paragraphs: highlighted });
    return t("OPO remis en rédaction : le dossier a changé son texte validé ({paragraphs}). Relisez-les, puis validez-le de nouveau.", { paragraphs: highlighted });
  }
  if (o.fromChief) {
    if (o.chief)
      return o.sections
        ? t("L'ordre de « {name} » a changé : cet ordre l'a suivi, {paragraphs} ({sections}).", { name: o.chief, paragraphs: updated, sections: o.sections })
        : t("L'ordre de « {name} » a changé : cet ordre l'a suivi, {paragraphs}.", { name: o.chief, paragraphs: updated });
    return o.sections
      ? t("L'ordre de l'échelon supérieur a changé : cet ordre l'a suivi, {paragraphs} ({sections}).", { paragraphs: updated, sections: o.sections })
      : t("L'ordre de l'échelon supérieur a changé : cet ordre l'a suivi, {paragraphs}.", { paragraphs: updated });
  }
  return o.sections
    ? t("L'ordre a suivi le dossier : {paragraphs} ({sections}).", { paragraphs: updated, sections: o.sections })
    : t("L'ordre a suivi le dossier : {paragraphs}.", { paragraphs: updated });
}
